/**
 * Pure text measurement — the paragraph box in world units, with no Babylon,
 * no scene and no fetched atlas.
 *
 * This is the arithmetic the Text behavior has always run in
 * `_measureContentBox`, lifted verbatim so a HEADLESS caller can run it too.
 * The behavior now delegates here, so there is exactly one implementation:
 * what the solver hugs at render time and what a tool measures before the
 * scene exists cannot drift apart.
 *
 * Everything it touches is Babylon-free: `textlayout` has zero imports,
 * `fontMetricsRegistry` pulls only textdata types, `fontRegistry` and the
 * generated metrics table, and `units` is pure conversion.
 */

import type { FontFamily, FontWeight, FontStyle } from '../../scenedata/behaviors/textdata';
import { computeTextLayout, computeLineBoxExtents, type TextLineBoxExtents, type TextLayout, type TextLayoutParams } from './textlayout';
import { TOFU_CHAR_ID, resolveFontMetrics, type FaceMetrics } from './fontMetricsRegistry';
import { fontSizeToWorldScale, anchorXFraction, computeAnchorYOffset } from './units';

/**
 * The layout unit: one millimetre, expressed in the metres the scene graph
 * uses. Layout sizes are authored and solved in millimetres — the wire format
 * carries metres, and a millimetre is the smallest step any authoring surface
 * can express — so a wrap constraint is quantised to a whole one of these
 * before it decides a line break ({@link quantiseWrapWidth}), and a line within
 * one of the constraint is treated as fitting ({@link wrapEpsilonFontUnits}).
 *
 * Stated as our own rule in `docs/layout/LAYOUT_ENGINE.md`, "The layout unit".
 */
export const LAYOUT_UNIT_M = 0.001;

/**
 * Float noise, in layout units, that a width may carry above a whole one and
 * still be that one: `4.001 / 0.001` is 4001.0000000000005, which a bare `ceil`
 * calls 4002. A nanometre — far below anything an authoring surface can express
 * and far above the ulps of the arithmetic, which are around 1e-11 units at
 * twenty metres.
 */
const QUANTISE_NOISE_UNITS = 1e-6;

export const DEFAULT_TEXT_FAMILY: FontFamily = 'Roboto';
export const DEFAULT_TEXT_WEIGHT: FontWeight = 'regular';
export const DEFAULT_TEXT_STYLE: FontStyle = 'normal';

/** The measured paragraph box, world units (metres). */
export interface MeasuredTextBox {
    /** Widest laid-out line, metres. */
    width: number;
    /** Line-box height: `lineCount × lineHeight × fontSize`, metres. */
    height: number;
    /** X of the glyph run's centre relative to the object origin, metres. */
    centerX: number;
    /** Y of the box centre relative to the object origin, metres. */
    centerY: number;
    /**
     * X of the centre layout places the node by. What `anchorX` anchors is the
     * SLOT — the width layout resolved for the paragraph — whenever there is
     * one, so `left` puts the paragraph at the slot's left edge however wide
     * the slot is, and `right` at its right edge. With no width (standalone
     * text, or a Hug width that is the text's own measurement) the box IS the
     * run and this equals {@link centerX}. `center` is zero either way.
     */
    layoutCenterX: number;
    /** Lines the text actually laid out into, after wrapping. */
    lineCount: number;
}

/** The authored fields a measurement depends on. Defaults match `TextData`. */
export interface TextMeasureInput {
    text: string;
    /** Spec `fontSize`, centi-world-units: 100 is a 1 m em-square. */
    fontSize: number;
    /** Em-multiplier, `TextData.lineHeight` default 1.2. */
    lineHeight?: number;
    /** Em units, `TextData.letterSpacing` default 0. */
    letterSpacing?: number;
    anchorX?: 'left' | 'center' | 'right';
    anchorY?: 'top' | 'middle' | 'bottom';
    fontFamily?: FontFamily;
    fontWeight?: FontWeight;
    fontStyle?: FontStyle;
    /** `false` is the buttons-and-badges opt-out: never wrap. */
    wrap?: boolean;
    /**
     * The width the layout resolved for this paragraph, metres — the Fixed or
     * Stretch constraint it must wrap inside. `null`/`undefined` means there is
     * none (standalone text, or a Hug width) and the text does not wrap.
     */
    wrapWidth?: number | null;
    /**
     * The slot layout actually imposed, metres, which `anchorX` aligns to.
     * Omitted, the slot is `wrapWidth`. `null` means no slot is imposed even
     * though `wrapWidth` may cap wrapping (a Hug maxWidth), so the run's own
     * box is what gets aligned.
     */
    slotWidth?: number | null;
}

/**
 * Resolve the face a paragraph measures against. Same fallback chain the
 * renderer resolves through, so measured face == rendered face.
 *
 * Does NOT trigger a hosted-font fetch: that is a runtime side effect and this
 * function is used headless. The behavior calls `ensureFontFamily` itself.
 */
export function resolveMeasureFace(input: Pick<TextMeasureInput, 'fontFamily' | 'fontWeight' | 'fontStyle'>): FaceMetrics {
    return resolveFontMetrics(
        input.fontFamily ?? DEFAULT_TEXT_FAMILY,
        input.fontWeight ?? DEFAULT_TEXT_WEIGHT,
        input.fontStyle ?? DEFAULT_TEXT_STYLE,
    );
}

/**
 * The distinct code points of `content` the face cannot draw. Whitespace and control characters are not glyphs.
 * A family the registry does not hold (a hosted pack) measures on Roboto and may well draw fine, so it is not judged.
 */
export function missingGlyphs(content: string, input: Pick<TextMeasureInput, 'fontFamily' | 'fontWeight' | 'fontStyle'>): number[] {
    const face = resolveMeasureFace(input);
    const family = input.fontFamily ?? DEFAULT_TEXT_FAMILY;
    const onDefault = (weight: TextMeasureInput['fontWeight'], style: TextMeasureInput['fontStyle']) =>
        face === resolveMeasureFace({ fontFamily: DEFAULT_TEXT_FAMILY, fontWeight: weight, fontStyle: style });
    if (family !== DEFAULT_TEXT_FAMILY && (onDefault(input.fontWeight, input.fontStyle) || onDefault(undefined, undefined))) return [];
    const missing = new Set<number>();
    for (const char of content) {
        const code = char.codePointAt(0)!;
        if (code <= 0x20 || (code >= 0x7f && code <= 0xa0) || /\s/u.test(char)) continue;
        if (face.fontApi.getChar(code).id === TOFU_CHAR_ID) missing.add(code);
    }
    return [...missing];
}

/**
 * Round a wrap constraint up to the next {@link LAYOUT_UNIT_M}.
 *
 * Rounding UP, not to nearest: a slot solved to 0.2999996 m is a 300 mm slot
 * that floating-point arithmetic landed just under, and rounding it down
 * breaks a line that fits. Without the quantisation a line overhanging its
 * slot by a fraction of a millimetre wraps in one solve and not in the next.
 *
 * A width already on the grid stays where it is: the noise a hair above a whole
 * unit is taken off before rounding up, so an exact 4.001 m is 4.001 m and not
 * 4.002. A positive width is never less than one unit.
 *
 * This is the width the wrap DECISION uses. Where the paragraph sits is the
 * slot as layout solved it, unrounded (`layoutCenterX`).
 */
export function quantiseWrapWidth(wrapWidthMetres: number): number {
    const units = Math.ceil(wrapWidthMetres / LAYOUT_UNIT_M - QUANTISE_NOISE_UNITS);
    return Math.max(1, units) * LAYOUT_UNIT_M;
}

/**
 * The wrap constraint in font units, or `undefined` when this paragraph must
 * not wrap. `wrap: false`, a degenerate fontSize and an absent width all mean
 * the same thing here.
 */
export function wrapWidthToFontUnits(
    wrapWidthMetres: number | null | undefined,
    emSize: number,
    worldScale: number,
    wrap?: boolean,
): number | undefined {
    if (wrap === false) return undefined;
    if (wrapWidthMetres == null || !(wrapWidthMetres > 0) || !(worldScale > 0)) return undefined;
    return (quantiseWrapWidth(wrapWidthMetres) / worldScale) * emSize;
}

/** Slack for the "already fits" fast path: one layout unit, in font units. */
export function wrapEpsilonFontUnits(emSize: number, worldScale: number): number {
    if (!(worldScale > 0)) return 0;
    return (LAYOUT_UNIT_M / worldScale) * emSize;
}

function finiteOr(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** A paragraph laid out in its face, with the mapping to the node's frame. */
export interface LaidOutTextBox {
    layout: TextLayout;
    box: TextLineBoxExtents;
    /** World size of one em (`fontSizeToWorldScale(fontSize)`), metres. */
    scale: number;
    /** Font units to ems (`1 / emSize`). */
    fontScale: number;
    /** Font units to metres in the node's frame. */
    unitScale: number;
    /** Y offset the anchor applies after scaling, metres. */
    yWorldOffset: number;
}

/**
 * Lay a paragraph out in its face: real advances and kerning from the
 * generated metrics table, and `lineHeight × fontSize` per line — the same
 * line-box rule `computeLineBoxExtents` runs for the renderer, so a headless
 * measure, an extruded body and a drawn paragraph cannot disagree.
 *
 * `face` may be supplied by a caller that already resolved one (the behavior
 * does, after kicking off a hosted-font fetch); otherwise it is resolved here.
 */
export function layOutTextBox(input: TextMeasureInput, face?: FaceMetrics): LaidOutTextBox {
    const resolved = face ?? resolveMeasureFace(input);
    const rawText = input.text ?? 'Text';
    const text = typeof rawText === 'string' ? rawText : String(rawText);

    const fontSize = finiteOr(input.fontSize, 14);
    const lineHeight = finiteOr(input.lineHeight, 1.2);
    const letterSpacingEm = finiteOr(input.letterSpacing, 0);
    const anchorX = input.anchorX ?? 'center';
    const anchorY = input.anchorY ?? 'middle';
    const scale = fontSizeToWorldScale(fontSize);

    const params: TextLayoutParams = {
        // lineHeight is em-relative in the spec; in font units that is just
        // lineHeight × emSize (no dependence on the font's natural line height).
        lineHeightUnits: lineHeight * resolved.emSize,
        letterSpacingUnits: letterSpacingEm * resolved.emSize,
        anchorXFraction: anchorXFraction(anchorX),
        maxWidthUnits: wrapWidthToFontUnits(input.wrapWidth, resolved.emSize, scale, input.wrap),
        wrapEpsilonUnits: wrapEpsilonFontUnits(resolved.emSize, scale),
    };

    const layout = computeTextLayout(text, params, resolved.fontApi);
    const box = computeLineBoxExtents(layout, anchorY, resolved.capTop);
    const fontScale = 1 / resolved.emSize;
    const yWorldOffset = computeAnchorYOffset(anchorY, box.yTop * fontScale, box.yBot * fontScale, scale);
    return { layout, box, scale, fontScale, unitScale: fontScale * scale, yWorldOffset };
}

/** Measure a paragraph's box (see {@link layOutTextBox}). */
export function measureTextBox(input: TextMeasureInput, face?: FaceMetrics): MeasuredTextBox {
    const { box, scale, fontScale, unitScale, yWorldOffset } = layOutTextBox(input, face);
    const anchorX = input.anchorX ?? 'center';
    const boxTopEm = box.yTop * fontScale;
    const boxBotEm = box.yBot * fontScale;
    const width = box.width * unitScale;
    const height = (boxTopEm - boxBotEm) * scale;

    // The slot, as layout solved it. Independent of `wrap`: a paragraph that
    // does not wrap still sits in its slot.
    const slot = input.slotWidth !== undefined ? input.slotWidth : input.wrapWidth;
    const slotWidth = slot != null && slot > 0 ? slot : width;

    return {
        width,
        height,
        centerX: (0.5 - anchorXFraction(anchorX)) * width,
        centerY: yWorldOffset + ((boxTopEm + boxBotEm) / 2) * scale,
        layoutCenterX: (0.5 - anchorXFraction(anchorX)) * slotWidth,
        lineCount: box.lineCount,
    };
}

/** Cache key covering every input `measureTextBox` reads. */
export function textMeasureCacheKey(input: TextMeasureInput): string {
    return [
        input.text, '\u0000',
        input.fontSize, '|', input.lineHeight, '|', input.letterSpacing, '|',
        input.anchorX, '|', input.anchorY, '|',
        input.fontFamily, '|', input.fontWeight, '|', input.fontStyle, '|',
        input.wrap === false ? 'nowrap' : '', '|',
        input.wrapWidth ?? '', '|',
        input.slotWidth === undefined ? 'same' : input.slotWidth ?? 'none',
    ].join('');
}
